/**
 * Composant ProjectStep - Gestion des étapes d'un projet
 * 
 * Ce composant gère la navigation entre les différentes étapes d'un projet.
 * Il utilise plusieurs hooks React pour optimiser les performances et l'expérience utilisateur :
 * 
 * - useTransition : Pour des transitions fluides entre étapes
 * - useDeferredValue : Pour améliorer les performances lors des changements
 * - useLayoutEffect : Pour des mises à jour rapides de l'interface
 * - useCallback/useMemo : Pour optimiser les re-renders
 * - useRef : Pour éviter les re-renders inutiles
 * 
 * Problème résolu : La navigation entre étapes nécessitait un rafraîchissement de la page
 * Solution : Utilisation de useEffect et useLayoutEffect pour détecter les changements
 * de paramètres et mettre à jour automatiquement l'état du composant.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect, useTransition, useDeferredValue, useId } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useProjectDetails } from '../../hooks/useProjectDetails';
import { useFormData } from '../../hooks/useFormData';
import { httpService } from '../../services/http.service';
import { API_ENDPOINTS } from '../../config/api.config';
import CadrageForm from '../Cadrage-projet/CadrageForm';
import CadrageBanner from '../Cadrage-projet/CadrageBanner';
import DimensionsPlans from '../DimensionsPlans';
import StepSectionSelector from '../../Components/StepSectionSelector';
import SectionContent from './SectionContent'; 
import './ProjectStep.css';
import { toast } from 'react-toastify';
import SuccessModal from './SuccessModal';
import { set } from 'react-hook-form';

const ProjectStep = () => {
  const { projectId, stepSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Désactiver complètement useProjectDetails pour éviter de charger tous les contenus
  // const { project: projectFromHook, steps: stepsFromHook, isLoading: hookLoading, error: hookError, submitContent } = useProjectDetails(projectId);
  
  // État pour les données brutes de l'API
  const [rawProjectData, setRawProjectData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Utiliser seulement les données brutes
  const project = rawProjectData;
  const steps = rawProjectData?.steps || [];
  const effectiveSteps = steps;
  const effectiveProject = project;
  
  // Charger les données brutes de l'API
  useEffect(() => {
    const fetchRawProjectData = async () => {
      if (!projectId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await httpService.get(API_ENDPOINTS.PROJECTS.DETAIL(projectId));
        setRawProjectData(data);
        console.log('=== DONNÉES BRUTES DU PROJET ===');
        console.log('Projet:', data.nom || data.name);
        console.log('Steps:', data.steps?.length || 0);
        if (data.steps && data.steps.length > 0) {
          data.steps.forEach((step, index) => {
            console.log(`Étape ${index + 1}:`, step.title);
            console.log(`Sections:`, step.sections?.length || 0);
            if (step.sections) {
              step.sections.forEach((section, sIndex) => {
                console.log(`  Section ${sIndex + 1}:`, section.title);
                console.log(`  Contenus:`, section.contents?.length || 0);
              });
            }
          });
        }
        console.log('===============================');
      } catch (error) {
        console.error('Erreur lors du chargement des données brutes:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchRawProjectData();
  }, [projectId]);

  // Plus besoin de fallback vers useProjectDetails
  const { 
    formData, 
    updateFormData, 
    submitDemande, 
    isSubmitting,
    addSelectedElement,
    getAllSelectedElements,
    loadFromLocalStorage,
    saveToLocalStorage,
    clearSavedData
  } = useFormData(projectId);

  // Au montage du composant, charger les données sauvegardées
  useEffect(() => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }

    const hasRestoredData = loadFromLocalStorage();
    if (hasRestoredData) {
      toast.info('Vos sélections précédentes ont été restaurées.');
    }
  }, [projectId, loadFromLocalStorage]);

  const [currentStep, setCurrentStep] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPending, startTransition] = useTransition();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successReference, setSuccessReference] = useState(null);
  
  // Référence pour éviter les re-renders inutiles
  const previousStepSlug = useRef(stepSlug);
  const previousLocation = useRef(location.pathname);

  // Utiliser useDeferredValue pour améliorer les performances
  const deferredStepSlug = useDeferredValue(stepSlug);

  // Utiliser useId pour l'accessibilité
  const stepNavigationId = useId();

  // Construire la liste complète des étapes avec Cadrage en premier
  const buildCompleteStepsList = useCallback(() => {
    if (!steps || steps.length === 0) return [];
    
    const completeSteps = [];
    
    // 1. Ajouter l'étape Cadrage (toujours en premier)
    completeSteps.push({
      id: 'cadrage',
      title: 'CADRAGE DU PROJET',
      name: 'CADRAGE DU PROJET',
      description: 'Cadrage initial du projet',
      isCadrage: true,
      order: 1
    });
    
    // 2. Ajouter les étapes récupérées de l'API
    effectiveSteps.forEach((step, index) => {
      completeSteps.push({
        ...step,
        order: index + 2, // Commencer à 2 car Cadrage est à 1
        isApiStep: true
      });
    });
    
    // 3. Ajouter l'étape Dimensions (toujours en dernier)
    completeSteps.push({
      id: 'dimensions',
      title: 'DIMENSIONS ET PLANS',
      name: 'DIMENSIONS ET PLANS',
      description: 'Finalisation des dimensions et plans',
      isDimensions: true,
      order: completeSteps.length + 1
    });
    
    console.log('Liste complète des étapes construite:', completeSteps);
    return completeSteps;
  }, [steps]);

  // Obtenir la liste complète des étapes avec useMemo pour optimiser
  const completeSteps = useMemo(() => buildCompleteStepsList(), [buildCompleteStepsList]);

  // Fonction pour trouver l'index d'une étape basée sur le slug
  const findStepIndexBySlug = useCallback((slug, stepsList) => {
    if (!stepsList || stepsList.length === 0) return 0;
    
    if (slug === 'cadrage') return 0;
    if (slug === 'dimensions') return stepsList.length - 1;
    
    return stepsList.findIndex(step => {
      if (step.isCadrage || step.isDimensions) return false;
      
      const stepName = (step.nom || step.name || step.title || '').toLowerCase();
      const stepSlugLower = slug.toLowerCase();
      
      return stepName.includes(stepSlugLower) || 
             stepSlugLower.includes(stepName) ||
             stepSlugLower === stepName.replace(/\s+/g, '-');
    });
  }, []);

  // Mettre à jour l'étape actuelle
  const updateCurrentStep = useCallback((slug, stepsList) => {
    if (!stepsList || stepsList.length === 0) return;
    
    const stepIndex = findStepIndexBySlug(slug, stepsList);
    if (stepIndex !== -1) {
      console.log(`Mise à jour de l'étape: index=${stepIndex}, slug=${slug}, titre=${stepsList[stepIndex]?.title}`);
      
      // Utiliser startTransition pour une mise à jour plus fluide
      startTransition(() => {
        setCurrentStepIndex(stepIndex);
        setCurrentStep(stepsList[stepIndex]);
      });
    }
  }, [findStepIndexBySlug, startTransition]);

  // useLayoutEffect pour une mise à jour plus rapide de l'interface
  useLayoutEffect(() => {
    if (completeSteps && completeSteps.length > 0 && previousStepSlug.current !== deferredStepSlug) {
      updateCurrentStep(deferredStepSlug, completeSteps);
      previousStepSlug.current = deferredStepSlug;
    }
  }, [deferredStepSlug, completeSteps, updateCurrentStep]);

  // Effet pour mettre à jour l'étape actuelle quand stepSlug change
  useEffect(() => {
    if (completeSteps && completeSteps.length > 0) {
      console.log('=== ÉTAPES COMPLÈTES DISPONIBLES ===');
      completeSteps.forEach((step, index) => {
        console.log(`Étape ${index}: ${step.title || step.name} (ID: ${step.id}, Type: ${step.isCadrage ? 'Cadrage' : step.isDimensions ? 'Dimensions' : 'API'})`);
      });
      console.log('=====================================');
    }
  }, [completeSteps]);

  // Sauvegarder les données à chaque changement du formulaire
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      saveToLocalStorage();
    }
  }, [formData, saveToLocalStorage]);

  // Effet pour forcer la mise à jour quand l'URL change
  useEffect(() => {
    if (completeSteps && completeSteps.length > 0 && previousLocation.current !== location.pathname) {
      console.log('ProjectStep - location changée:', location.pathname);
      updateCurrentStep(deferredStepSlug, completeSteps);
      previousLocation.current = location.pathname;
    }
  }, [location.pathname, deferredStepSlug, completeSteps, updateCurrentStep]);

  // Effet pour initialiser currentStep lors du premier chargement
  useEffect(() => {
    if (completeSteps && completeSteps.length > 0 && !currentStep) {
      console.log('Initialisation de currentStep lors du premier chargement');
      console.log('StepSlug:', stepSlug);
      console.log('CompleteSteps:', completeSteps);
      updateCurrentStep(stepSlug, completeSteps);
    }
  }, [completeSteps, stepSlug, currentStep, updateCurrentStep]);

  // Log de débogage pour l'état du composant
  useEffect(() => {
    console.log('=== ÉTAT DU COMPOSANT ===');
    console.log('Project:', project);
    console.log('Steps:', steps);
    console.log('CompleteSteps:', completeSteps);
    console.log('CurrentStep:', currentStep);
    console.log('CurrentStepIndex:', currentStepIndex);
    console.log('StepSlug:', stepSlug);
    console.log('========================');
  }, [project, steps, completeSteps, currentStep, currentStepIndex, stepSlug]);

  // Composant de débogage temporaire (à retirer en production)
  const DebugInfo = () => (
    <div className="alert alert-info mt-3">
      <h6>Debug Info:</h6>
      <p><strong>Project:</strong> {project ? 'Chargé' : 'Non chargé'}</p>
      <p><strong>Steps:</strong> {steps ? steps.length : 0}</p>
      <p><strong>CompleteSteps:</strong> {completeSteps ? completeSteps.length : 0}</p>
      <p><strong>CurrentStep:</strong> {currentStep ? 'Défini' : 'Non défini'}</p>
      <p><strong>CurrentStepIndex:</strong> {currentStepIndex}</p>
      <p><strong>StepSlug:</strong> {stepSlug}</p>
      <p><strong>IsLoading:</strong> {isLoading ? 'Oui' : 'Non'}</p>
      <p><strong>Error:</strong> {error || 'Aucune'}</p>
    </div>
  );

  // Composant PdfUploader amélioré
  const PdfUploader = () => {
    const [files, setFiles] = useState([]);

    const handleFileChange = (e) => {
      const selectedFiles = Array.from(e.target.files);
      setFiles(selectedFiles);
    };

    const removeFile = (index) => {
      setFiles(files.filter((_, i) => i !== index));
    };

    return (
      <div>
        <input
          id="fileUpload"
          type="file"
          multiple
          accept="application/pdf"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        
        {files.length > 0 && (
          <div style={{ marginTop: '15px' }}>
            <strong>Fichiers sélectionnés :</strong>
            <ul className="list-unstyled mt-2">
              {files.map((file, index) => (
                <li key={index} className="d-flex align-items-center justify-content-between p-2 border rounded mb-2">
                  <span className="text-truncate me-2">
                    <i className="fas fa-file-pdf text-danger me-2"></i>
                    {file.name}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeFile(index)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Valider la possibilité de quitter l'étape courante
  const canProceedFromCurrentStep = useCallback(() => {
    if (!currentStep) return false;

    // Validation pour l'étape cadrage (exemple)
    if (currentStep.isCadrage) {
      const required = ['name', 'email', 'numero_tel', 'adresse'];
      const missing = required.filter(f => !formData?.[f] || formData[f] === '');
      if (missing.length > 0) {
        toast.warn(`Veuillez remplir les champs : ${missing.join(', ')}`);
        return false;
      }
    }

    // Validation simple pour étapes API/sections : au moins une sélection pour cette étape
    if (currentStep.sections && currentStep.sections.length > 0) {
      const selectedForStep = getAllSelectedElements().filter(e => e.stepId === currentStep.id);
      if (selectedForStep.length === 0) {
        toast.warn('Veuillez renseigner des informations et sélectionner des éléments pour continuer.');
        return false;
      }
    }

    // Par défaut autoriser
    return true;
  }, [currentStep, formData, getAllSelectedElements]);

  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < completeSteps.length) {

      const step = completeSteps[stepIndex];
      let slug = 'cadrage';
      
      if (step.isCadrage) {
        slug = 'cadrage';
      } else if (step.isDimensions) {
        slug = 'dimensions';
      } else {
        const stepName = (step.nom || step.name || step.title || '');
        if (stepName) {
          slug = stepName.replace(/\s+/g, '-').toLowerCase();
        } else {
          slug = step.id.toString();
        }
      }
      
      console.log(`Navigation vers l'étape ${stepIndex}: ${step.title || step.name} avec le slug: ${slug}`);
      navigate(`/projet/${projectId}/etape/${slug}`);
    }
  }, [completeSteps, navigate, projectId]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      goToStep(currentStepIndex - 1);
    }
  }, [currentStepIndex, goToStep]);

  const goToNextStep = useCallback(() => {
    if (currentStepIndex < completeSteps.length - 1) {
      // Empêcher la navigation si la validation échoue
      if (!canProceedFromCurrentStep()) return;
      goToStep(currentStepIndex + 1);
    }
  }, [currentStepIndex, completeSteps.length, goToStep, canProceedFromCurrentStep]);

  // Gestionnaire pour les changements de données des formulaires
  const handleFormDataChange = useCallback((newData) => {
    console.log('Données reçues du formulaire:', newData);
    // Mettre à jour les données du formulaire
    Object.keys(newData).forEach(key => {
      console.log(`Mise à jour du champ ${key}:`, newData[key]);
      updateFormData(key, newData[key]);
    });
  }, [updateFormData]);

  // Gestionnaire spécifique pour les données de cadrage
  const handleCadrageDataChange = useCallback((newData) => {
    console.log('Données de cadrage reçues:', newData);
    // Mettre à jour les données du formulaire
    Object.keys(newData).forEach(key => {
      console.log(`Mise à jour du champ de cadrage ${key}:`, newData[key]);
      updateFormData(key, newData[key]);
    });
  }, [updateFormData]);

  // Gestionnaire spécifique pour les données de dimensions
  const handleDimensionsDataChange = useCallback((newData) => {
    console.log('Données de dimensions reçues:', newData);
    // Mettre à jour les données du formulaire
    Object.keys(newData).forEach(key => {
      console.log(`Mise à jour du champ de dimensions ${key}:`, newData[key]);
      updateFormData(key, newData[key]);
    });
  }, [updateFormData]);

  // Gestionnaire pour les sélections dans les sections
  const handleSelectionChange = useCallback((newSelections) => {
    console.log('=== SÉLECTION CHANGÉE ===');
    console.log('Nouvelles sélections (format StepSectionSelector):', newSelections);
    console.log('Nombre de contenus sélectionnés:', Object.keys(newSelections).length);
    console.log('Étape courante:', currentStep);
    
    // Convertir le format StepSectionSelector vers le format useFormData
    Object.entries(newSelections).forEach(([contentId, contentData]) => {
      console.log(`Traitement du contenu ${contentId}:`, contentData);
      
      if (currentStep && currentStep.id) {
        const stepId = currentStep.id;
        // Utiliser un index fixe pour la section (0) au lieu de l'ID de l'API
        const sectionId = '0';
        
        console.log(`Ajout de l'élément ${contentId} à l'étape ${stepId}, section ${sectionId}`);
        console.log('Données de l\'élément:', {
          id: contentId,
          ...contentData
        });
        
        // Ajouter l'élément avec l'ID du contenu
        addSelectedElement(stepId, sectionId, contentId, {
          id: contentId,
          ...contentData
        });
        
        console.log('Élément ajouté avec succès');
      } else {
        console.warn('Aucune étape courante définie');
      }
    });
    
    // Vérifier les sélections après ajout
    const allSelectedElements = getAllSelectedElements();
    console.log('=== SÉLECTIONS APRÈS AJOUT ===');
    console.log('Nombre total d\'éléments sélectionnés:', allSelectedElements.length);
    console.log('Détail des sélections:', allSelectedElements);
  }, [addSelectedElement, currentStep, getAllSelectedElements]);

  // Soumettre la demande
  const handleSubmitDemande = useCallback(async () => {
    try {
      // Récupérer les données actuelles du formulaire
      const currentFormData = formData;
      console.log('=== DÉBUT SOUMISSION DEMANDE ===');
      console.log('Données actuelles du formulaire:', currentFormData);
      
      // Récupérer tous les éléments sélectionnés via useFormData
      const allSelectedElements = getAllSelectedElements();
      console.log('=== ÉLÉMENTS SÉLECTIONNÉS ===');
      console.log('Nombre d\'éléments:', allSelectedElements.length);
      console.log('Détail des éléments:', allSelectedElements);
      console.log('Structure des données du formulaire:', currentFormData);
      console.log('StepData dans le formulaire:', currentFormData.stepData);
      
      // Vérifier que les données essentielles sont présentes
      const requiredFields = ['name', 'email', 'numero_tel', 'adresse'];
      const missingFields = requiredFields.filter(field => !currentFormData[field] || currentFormData[field] === '');
      
      if (missingFields.length > 0) {
        console.error('Champs manquants dans le formulaire:', missingFields);
        console.log('Valeurs actuelles des champs manquants:', missingFields.map(field => ({ [field]: currentFormData[field] })));
        alert(`Veuillez remplir les champs suivants: ${missingFields.join(', ')}`);
        return;
      }
      
      // Inclure les sélections dans les données à soumettre
      const demandeData = {
        ...currentFormData,
        selectedElements: allSelectedElements
      };
      
      console.log('Données complètes à soumettre:', demandeData);
      console.log('Données personnelles:', {
        name: demandeData.name,
        email: demandeData.email,
        numero_tel: demandeData.numero_tel,
        adresse: demandeData.adresse,
        project_step: demandeData.project_step
      });
      
      const result = await submitDemande(demandeData);
      const reference = result.data?.reference_chantier;
      if (result.success) {
        console.log('Demande soumise avec succès:', result.data);

        // --- NOUVEAU : demander la génération du PDF côté backend ---
        try {
          // Extraire l'ID de la demande renvoyée par l'API 
          const demandeId = result.data?.id || result.data?.demande_id || result.data;
          if (demandeId) {
            console.log('Appel generation PDF pour la demande id=', demandeId);
            // POST vers /be/generate-pdf/{demande_id}/
            console.log('ref_chantier=', result.data?.reference_chantier);
            setSuccessReference(result.data?.reference_chantier);
            await httpService.post(`/be/generate-pdf/${demandeId}/`, { id: demandeId });
            // toast.success('Demande envoyée avec succès');
            
            console.log('Requête de génération PDF envoyée avec succès pour id=', demandeId);
          } else {
            console.warn('Impossible de récupérer l\'ID de la demande pour la génération PDF', result.data);
          }
        } catch (pdfError) {
          console.error('Erreur lors de l\'appel de génération PDF:', pdfError);
          // Ne pas bloquer la navigation principale si la génération de PDF échoue
        }
        // ------------------------------------------------------------

        // TODO: Aouter l'ouverture de la success modal ici
        setIsSuccessModalOpen(true);

        // Rediriger vers une page de confirmation ou retourner à l'accueil
        // navigate('/');
      } else {
        console.error('Erreur lors de la soumission:', result.error);
        // alert(`Erreur lors de la soumission: ${result.error}`);
      }
    } catch (error) {
      console.error('Erreur lors de la soumission de la demande:', error);
      // alert(`Erreur lors de la soumission: ${error.message}`);
    }
  }, [formData, getAllSelectedElements, submitDemande, navigate]);

  // Ajouter un gestionnaire pour la fermeture de la modale
  const handleModalClose = useCallback(() => {
    setIsSuccessModalOpen(false);
    setSuccessReference(null);
    clearSavedData(); // <- Nettoyer localStorage
    // Rediriger vers la page d'accueil après fermeture de la modale
    navigate('/');
  }, [navigate, clearSavedData]);

  const handleAbandonDemande = useCallback(() => {
    if (window.confirm('Êtes-vous sûr ? Les données seront supprimées.')) {
      clearSavedData();
      navigate('/');
    }
  }, [clearSavedData, navigate]);
  

  if (isLoading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h5>Erreur lors du chargement du projet</h5>
          <p><strong>Message:</strong> {error}</p>
          <button 
            className="btn btn-outline-secondary mt-2"
            onClick={() => navigate('/')}
          >
            ← Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  if (!project || !currentStep) {
    // Si les étapes sont en cours de chargement, afficher un indicateur de chargement
    if (completeSteps && completeSteps.length > 0) {
      return (
        <div className="container mt-4">
          <div className="text-center">
            <div className="spinner-border text-info" role="status">
              <span className="visually-hidden">Chargement de l'étape...</span>
            </div>
            <p className="mt-2">Chargement de l'étape...</p>
            <p className="text-muted">Étapes disponibles: {completeSteps.length}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          <h5>Étape non trouvée</h5>
          <p><strong>Project ID:</strong> {projectId}</p>
          <p><strong>Step Slug:</strong> {stepSlug}</p>
          <p><strong>Étapes chargées:</strong> {completeSteps ? completeSteps.length : 0}</p>
          <p><strong>Current Step:</strong> {currentStep ? 'Défini' : 'Non défini'}</p>
          <button 
            className="btn btn-outline-secondary mt-2"
            onClick={() => navigate('/')}
          >
            ← Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  // Afficher un indicateur de transition si une étape est en cours de chargement
  if (isPending) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-info" role="status">
            <span className="visually-hidden">Chargement de l'étape...</span>
          </div>
          <p className="mt-2">Chargement de l'étape...</p>
        </div>
      </div>
    );
  }

  // Si c'est l'étape Cadrage (première étape), afficher le contenu Cadrage complet
  if (stepSlug === 'cadrage') {
    return (
      <div className="project-step-container">
        {/* En-tête du projet */}
        {/*section banner*/}
        <section className="be-banner" style={{ backgroundImage: `${project.image}` }}>
          <div className="">
            <p className="be-banner-title">CHIFFRAGE : {project.nom || project.name}</p>
            <p className="be-centered-p">CADRAGE DU PROJET</p>
          </div>
        </section>
        {/* <div className="project-header">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h1 className="project-title">{project.nom || project.name}</h1>
                <p className="project-description">{project.description}</p>
              </div>
              <div className="col-md-4 text-end">
                <button 
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/')}
                >
                  ← Retour aux projets
                </button>
              </div>
            </div>
          </div>
        </div> */}

        {/* Composant de débogage temporaire */}
        {/* <DebugInfo /> */}

        {/* Navigation des étapes */}
        {/* <div className="steps-navigation">
          <div className="container">
            <div className="steps-progress" role="tablist" aria-label="Navigation des étapes du projet" id={stepNavigationId}>
              {completeSteps.map((step, index) => {
                const stepName = (step.nom || step.name || step.title || '').toLowerCase();
                let slug = 'cadrage';
                
                if (step.isCadrage) {
                  slug = 'cadrage';
                } else if (step.isDimensions) {
                  slug = 'dimensions';
                } else {
                  slug = stepName.replace(/\s+/g, '-').toLowerCase();
                }
                
                return (
                  <div
                    key={step.id}
                    className={`step-indicator ${index === currentStepIndex ? 'active' : ''} ${index < currentStepIndex ? 'completed' : ''}`}
                    onClick={() => goToStep(index)}
                    role="tab"
                    aria-selected={index === currentStepIndex}
                    aria-controls={`step-${step.id}-content`}
                    id={`step-${step.id}-tab`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goToStep(index);
                      }
                    }}
                  >
                    <div className="step-number">{step.order}</div>
                    <div className="step-label">
                      {step.title || step.name}
                      {step.isCadrage && <span className="badge bg-success ms-2">Cadrage</span>}
                      {step.isDimensions && <span className="badge bg-info ms-2">Dimensions</span>}
                      {step.isApiStep && <span className="badge bg-primary ms-2">API</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div> */}

        {/* Contenu Cadrage avec gestion des données */}
        <div className="cadrage-content py-5">
          <CadrageForm 
            onDataChange={handleCadrageDataChange}
            formData={formData}
          />

          {/* Navigation entre étapes */}
          <div className="mt-5 d-flex justify-content-center" style={{flexDirection: 'initial'}}>
            <div className="d-flex justify-content-center" style={{width: 'fit-content', flexDirection: 'initial'}}>
              {/* {currentStepIndex > 0 && ( */}
                <div
                 className="bois-de-pays me-2" 
                 onClick={() => navigate('/')}
                >
                  <a className="text-2" href="#">
                    PRECEDENT
                  </a>
                </div>
                {/* Bouton Abandonner */}
                
              {/* )} */}
              {completeSteps.length > 1 && (
                <div
                 className="btn btn-secondary box-green rounded-0"
                  onClick={goToNextStep}
                >
                  <a
                    className="text-2"
                  >
                    SUIVANT
                  </a>
                </div>
              )}
              
            </div>
          </div>

          {/* <div className="step-navigation">
            <div className="container">
              <div className="row">
                <div className="col-md-6">
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/')}
                  >
                    ← Retour aux projets
                  </button>
                </div>
                <div className="col-md-6 text-end">
                  {completeSteps.length > 1 && (
                    <button 
                      className="btn btn-primary"
                      onClick={goToNextStep}
                    >
                      Étape suivante →
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    );
  }

  // Si c'est l'étape Dimensions (dernière étape), afficher le contenu DimensionsPlans
  if (stepSlug === 'dimensions') {
  return (
    <div className="project-step-container">
      {/* En-tête du projet */}
      <section className="be-banner" style={{ backgroundImage: `${project.image}` }}>
        <div className="">
          <p className="be-banner-title">CHIFFRAGE : {project.nom || project.name}</p>
          <p className="be-centered-p">DIMENSIONS ET PLANS</p>
        </div>
      </section>

      {/* <div className="project-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <h1 className="project-title">{project.nom || project.name}</h1>
              <p className="project-description">{project.description}</p>
            </div>
            <div className="col-md-4 text-end">
              <button 
                className="btn btn-outline-secondary"
                onClick={() => navigate('/')}
              >
                ← Retour aux projets
              </button>
            </div>
          </div>
        </div>
      </div> */}

        {/* Composant de débogage temporaire */}
        {/* <DebugInfo /> */}

      {/* Navigation des étapes */}
      {/* <div className="steps-navigation">
        <div className="container">
            <div className="steps-progress" role="tablist" aria-label="Navigation des étapes du projet" id={stepNavigationId}>
            {completeSteps.map((step, index) => {
              const stepName = (step.nom || step.name || step.title || '').toLowerCase();
              let slug = 'cadrage';
              
              if (step.isCadrage) {
                slug = 'cadrage';
              } else if (step.isDimensions) {
                slug = 'dimensions';
              } else {
                slug = stepName.replace(/\s+/g, '-').toLowerCase();
              }
              
              return (
                <div
                  key={step.id}
                  className={`step-indicator ${index === currentStepIndex ? 'active' : ''} ${index < currentStepIndex ? 'completed' : ''}`}
                  onClick={() => goToStep(index)}
                    role="tab"
                    aria-selected={index === currentStepIndex}
                    aria-controls={`step-${step.id}-content`}
                    id={`step-${step.id}-tab`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goToStep(index);
                      }
                    }}
                >
                  <div className="step-number">{step.order}</div>
                  <div className="step-label">
                    {step.title || step.name}
                    {step.isCadrage && <span className="badge bg-success ms-2">Cadrage</span>}
                    {step.isDimensions && <span className="badge bg-info ms-2">Dimensions</span>}
                    {step.isApiStep && <span className="badge bg-primary ms-2">API</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div> */}

        {/* Contenu DimensionsPlans avec gestion des données */}
        <div className="dimensions-content py-5">
          <DimensionsPlans onDataChange={handleDimensionsDataChange} />
          
          <SuccessModal 
            show={isSuccessModalOpen} 
            onClose={handleModalClose}
            reference={successReference}
          />

          {/* Navigation entre étapes */}
          <div className="mt-5 d-flex justify-content-center" style={{flexDirection: 'initial'}}>
            <div className="d-flex justify-content-center" style={{width: 'fit-content', flexDirection: 'initial'}}>
              {currentStepIndex > 0 && (
                <div className="bois-de-pays me-2" onClick={goToPreviousStep}>
                  <a className="text-2" href="#">
                    PRECEDENT
                  </a>
                </div>
              )}
              {/* Bouton Abandonner */}
              
              {/* {currentStepIndex < completeSteps.length - 1 && ( */}
                <div
                 className="btn btn-secondary box-green rounded-0" 
                //  className="btn btn-success"
                  onClick={handleSubmitDemande}
                  disabled={isSubmitting}
                >
                  <a
                    className="text-2"
                  >
                    {isSubmitting ? 'Soumission...' : 'SOUMETTRE'}
                  </a>
                </div>
              {/* )} */}
              
            </div>
          </div>

          {/* <div className="step-navigation">
            <div className="container">
            <div className="row">
              <div className="col-md-6">
                {currentStepIndex > 0 && (
                  <div className="bois-de-pays me-2" onClick={goToPreviousStep}>
                    <a className="text-2" href="#">
                      PRECEDENT
                    </a>
                  </div>
                )}
              </div>
              <div className="col-md-6 text-end">
                <button 
                  className="btn btn-info me-2"
                  onClick={() => {
                    console.log('Données actuelles du formulaire:', formData);
                    alert(`Données du formulaire:\nNom: ${formData.name}\nEmail: ${formData.email}\nTéléphone: ${formData.numero_tel}\nAdresse: ${formData.adresse}\nÉtape: ${formData.project_step}`);
                  }}
                >
                  Vérifier les données
                </button>
                <button 
                  className="btn btn-warning me-2"
                  onClick={() => {
                    const allElements = getAllSelectedElements();
                    console.log('=== TEST SÉLECTIONS ACTUELLES ===');
                    console.log('Nombre d\'éléments:', allElements.length);
                    console.log('Détail des éléments:', allElements);
                    
                    // Afficher chaque élément individuellement
                    allElements.forEach((element, index) => {
                      console.log(`Élément ${index + 1}:`, element);
                      console.log(`  - stepId: ${element.stepId}`);
                      console.log(`  - sectionId: ${element.sectionId}`);
                      console.log(`  - contentId: ${element.id}`);
                      console.log(`  - contentType: ${element.contentType}`);
                      console.log(`  - selections:`, element.selections);
                      console.log(`  - inputs:`, element.inputs);
                      console.log(`  - otherChoices:`, element.otherChoices);
                      console.log(`  - textContent:`, element.textContent);
                    });
                    
                    console.log('Structure stepData:', formData.stepData);
                    alert(`Sélections actuelles: ${allElements.length} éléments\nVoir la console pour plus de détails`);
                  }}
                >
                  Test Sélections
                </button>
                <button 
                  className="btn btn-success"
                  onClick={handleSubmitDemande}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Soumission...' : 'Finaliser le projet'}
                </button>
                {submitError && (
                  <div className="alert alert-danger mt-2">
                    Erreur lors de la soumission: {submitError}
                  </div>
                )}
                {submitSuccess && (
                  <div className="alert alert-success mt-2">
                    Demande soumise avec succès!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
  }

  // Vérification de chargement
  if (isLoading) {
    return (
      <div className="project-step-container">
        <div className="container">
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-3">Chargement du projet...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-step-container">
        <div className="container">
          <div className="alert alert-danger">
            <h4>Erreur de chargement</h4>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!project || !steps.length) {
    return (
      <div className="project-step-container">
        <div className="container">
          <div className="alert alert-warning">
            <h4>Projet non trouvé</h4>
            <p>Le projet demandé n'a pas pu être chargé.</p>
          </div>
        </div>
      </div>
    );
  }

  // Vérifier que les données brutes sont chargées pour le filtrage correct
  if (!rawProjectData) {
    return (
      <div className="project-step-container">
        <div className="container">
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-3">Chargement des données du projet...</p>
          </div>
        </div>
      </div>
    );
  }

  // Pour les autres étapes, afficher le contenu normal
  return (
    <div className="project-step-container">
      {/* En-tête du projet */}
      {/* <div className="project-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <h1 className="project-title">{effectiveProject.nom || effectiveProject.name}</h1>
              <p className="project-description">{effectiveProject.description}</p>
                  </div>
            <div className="col-md-4 text-end">
              <button 
                className="btn btn-outline-secondary"
                onClick={() => navigate('/')}
              >
                ← Retour aux projets
              </button>
            </div>
          </div>
        </div>
      </div> */}

      {/* Composant de débogage temporaire */}
      {/* <DebugInfo /> */}

      {/* Navigation des étapes */}
      {/* <div className="steps-navigation">
        <div className="container">
          <div className="steps-progress" role="tablist" aria-label="Navigation des étapes du projet" id={stepNavigationId}>
            {completeSteps.map((step, index) => {
              const stepName = (step.nom || step.name || step.title || '').toLowerCase();
              let slug = 'cadrage';
              
              if (step.isCadrage) {
                slug = 'cadrage';
              } else if (step.isDimensions) {
                slug = 'dimensions';
              } else {
                slug = stepName.replace(/\s+/g, '-').toLowerCase();
              }
              
              return (
                <div
                  key={step.id}
                  className={`step-indicator ${index === currentStepIndex ? 'active' : ''} ${index < currentStepIndex ? 'completed' : ''}`}
                  onClick={() => goToStep(index)}
                  role="tab"
                  aria-selected={index === currentStepIndex}
                  aria-controls={`step-${step.id}-content`}
                  id={`step-${step.id}-tab`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      goToStep(index);
                    }
                  }}
                >
                  <div className="step-number">{step.order}</div>
                  <div className="step-label">
                    {step.title || step.name}
                    {step.isCadrage && <span className="badge bg-success ms-2">Cadrage</span>}
                    {step.isDimensions && <span className="badge bg-info ms-2">Dimensions</span>}
                    {step.isApiStep && <span className="badge bg-primary ms-2">API</span>}
                </div>
                    </div>
              );
            })}
              </div>
          </div>
      </div> */}
      <CadrageBanner 
        projectName={project.nom || project.name}
        projectStep={currentStep.title || currentStep.name}
        projectImage={project.image}
      />

      {/* Contenu de l'étape */}
      <div className="step-content">
        <div className="container">
          {/* <div className="step-header">
            <h2 className="step-title">
              {currentStep.nom || currentStep.name}
              {currentStep.isDefault && <span className="badge bg-info ms-2">Étape par défaut</span>}
            </h2>
            <p className="step-description">{currentStep.description}</p>
          </div> */}

          {/* Composant de test API pour déboguer */}
          {/* <ApiTestComponent /> */}

                     {/* Affichage des sections de l'étape avec sélection d'éléments */}
           {currentStep.sections && currentStep.sections.length > 0 ? (
             <div className="sections-container">
               {console.log(`Affichage de ${currentStep.sections.length} sections pour l'étape ${currentStep.id}:`, currentStep.sections)}
               <SectionContent 
                 projectId={projectId}
                 currentStep={currentStep}
                 handleSelectionChange={handleSelectionChange}
                 getAllSelectedElements={getAllSelectedElements}
               />
               
               {/* Affichage des éléments sélectionnés pour cette étape */}
               {/* <div className="selected-elements-summary mt-4">
                 <h5>Éléments sélectionnés pour cette étape :</h5>
                 {(() => {
                   const allElements = getAllSelectedElements();
                   const stepElements = allElements.filter(element => element.stepId === currentStep.id);
                   return stepElements.length > 0 ? (
                     <div className="selected-items-list">
                       {stepElements.map((element, index) => (
                         <div key={index} className="selected-item-card">
                           <span className="item-name">{element.title || element.id}</span>
                           <span className="item-section">Section: {element.sectionId}</span>
                         </div>
                       ))}
                     </div>
                   ) : (
                     <p className="text-muted">Aucun élément sélectionné pour cette étape.</p>
                   );
                 })()}
               </div> */}
             </div>
          ) : (
            <div className="no-sections">
              {console.log(`Aucune section pour l'étape ${currentStep.id}:`, currentStep)}
              <p className="text-center text-muted">
                Aucune section configurée pour cette étape.
              </p>
              <div className="text-center mt-3">
                <small className="text-muted">
                  ID de l'étape: {currentStep.id} | Nom: {currentStep.title || currentStep.name}
                </small>
              </div>
            </div>
          )}

          {/* Navigation entre étapes */}
          <div className="mt-5 d-flex justify-content-center" style={{flexDirection: 'initial'}}>
            <div className="d-flex justify-content-center" style={{width: 'fit-content', flexDirection: 'initial'}}>
              {currentStepIndex > 0 && (
                <div className="bois-de-pays me-2" onClick={goToPreviousStep}>
                  <a className="text-2" href="#">
                    PRECEDENT
                  </a>
                </div>
              )}
              {/* Bouton Abandonner */}
              {/* <div className="btn btn-outline-danger rounded-0 me-2" onClick={handleAbandonDemande}>
                <a className="text-2" href="#">ABANDONNER</a>
              </div> */}
              {currentStepIndex < completeSteps.length - 1 && (
                <div className="btn btn-secondary box-green rounded-0" onClick={goToNextStep}>
                  <a
                    className="text-2"
                  >
                    SUIVANT
                  </a>
                </div>
              )}
              
            </div>
          </div>
          {/* <div className="step-navigation">
            <div className="row">
              <div className="col-md-6">
                {currentStepIndex > 0 && (
                  
                  <button 
                    className="btn btn-outline-primary"
                    onClick={goToPreviousStep}
                  >
                    ← Étape précédente
                  </button>
                )}
              </div>
              <div className="col-md-6 text-end">
                {currentStepIndex < completeSteps.length - 1 && (
                  <button 
                    className="btn btn-primary"
                    onClick={goToNextStep}
                  >
                    Étape suivante →
                  </button>
                )}
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

// Composants supprimés car non utilisés :
// - ContentRenderer : Rendu des différents types de contenu selon le schéma UML
// - BlockSection : Affichage des sections comme sur la page Charpente

export default ProjectStep;